import React, { useMemo, useRef } from 'react';
import { FlagFilled } from 'shared/assets/icons';
import { GoalContextMenu } from 'shared/components/connected/goal-context-menu';
import { CircleProgress } from 'shared/components/ui/circle-progress';
import { useTimeBasedStyle } from 'shared/components/ui/full-calendar-event/use-time-based-style';
import { Icon } from 'shared/components/ui/icon';
import { Tooltip } from 'shared/components/ui/tooltip';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { getCompletionPercentage } from 'shared/utils/get-completion-percentage';
import { getGoalCompletion } from 'shared/utils/get-goal-completion';
import { getLinkedItemColor } from 'shared/utils/get-linked-item-color';

import * as Styled from './event.style';

export type GoalEventProps = {
  goalId: ID;
  startDate?: string;
  endDate?: string;
  goals: Goal[];
  lifeAreas: LifeArea[];
  onClick?: (id: ID) => void;
  isDeadline?: boolean;
};

export const GoalEvent: React.FC<GoalEventProps> = ({
  goalId,
  startDate,
  endDate,
  goals,
  lifeAreas,
  onClick,
  isDeadline,
}) => {
  const goal = useMemo(
    () => goals.find(({ id }) => id === goalId),
    [goals, goalId],
  );

  const {
    open: openMenu,
    close: closeMenu,
    position: menuPosition,
  } = useContextMenuState();
  const containerRef = useRef<HTMLDivElement>(null);

  const { marginTop, lineHeight, numberOfLines } = useTimeBasedStyle(
    startDate,
    endDate,
  );

  const completionPercentage = useMemo(
    () =>
      goal
        ? getCompletionPercentage(
            getGoalCompletion(goal, {
              goals,
            }),
          )
        : undefined,
    [goal, goals],
  );

  const color = useMemo(
    () =>
      goal
        ? getLinkedItemColor(goal.id, { goals, lifeAreas }) ?? undefined
        : undefined,
    [goal, goals, lifeAreas],
  );

  if (!goal || !startDate) {
    return null;
  }

  const _onClick = () => goal && onClick?.(goal.id);

  return (
    <>
      <Styled.Container
        onClick={_onClick}
        onContextMenu={openMenu}
        $color={color}
        $completed={false}
        ref={containerRef}
      >
        <Styled.Content>
          <Styled.IconContainer $color={color}>
            <CircleProgress
              percentage={completionPercentage}
              hidePercentage
              isSmall
            />
          </Styled.IconContainer>
          <Styled.Title
            $marginTop={marginTop}
            $lineHeight={lineHeight}
            $completed={false}
            $numberOfLines={numberOfLines}
          >
            {goal.name}
          </Styled.Title>
          {isDeadline && (
            <Styled.DeadlineIconContainer $color={color}>
              <Icon icon={FlagFilled} />
            </Styled.DeadlineIconContainer>
          )}
        </Styled.Content>
      </Styled.Container>

      {!!menuPosition && (
        <GoalContextMenu
          id={goal.id}
          location={menuPosition}
          onClose={closeMenu}
        />
      )}
      <Tooltip referenceElement={containerRef} label={goal.name} delay={400} />
    </>
  );
};
